import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon, PencilIcon, TrashIcon, FolderIcon } from '@heroicons/react/24/outline';
import { fetchCategories, createCategory, updateCategory, deleteCategory } from '../../store/slices/categorySlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Table, Button, Badge, SearchInput, Pagination, ConfirmDialog, Card } from '../../components/Common';
import CategoryModal from '../../components/Modals/CategoryModal';

const CategoryList = () => {
  const dispatch = useDispatch();
  const { categories, loading, pagination } = useSelector((state) => state.category);
  
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => {
    dispatch(fetchCategories({ page, size: pageSize, search }));
  }, [dispatch, page, pageSize, search]);

  const openModal = (category = null) => {
    setEditingCategory(category);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCategory(null);
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingCategory) {
        await dispatch(updateCategory({ id: editingCategory.id, data: formData })).unwrap();
        dispatch(showSuccess('Category updated successfully'));
      } else {
        await dispatch(createCategory(formData)).unwrap();
        dispatch(showSuccess('Category created successfully'));
      }
      closeModal();
      dispatch(fetchCategories({ page, size: pageSize, search }));
    } catch (error) {
      console.error('Submit error:', error);
      dispatch(showError(error || 'Failed to save category'));
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteCategory(deleteModal.id)).unwrap();
        dispatch(showSuccess('Category deleted successfully'));
        dispatch(fetchCategories({ page, size: pageSize, search }));
      } catch (error) {
        dispatch(showError(error || 'Failed to delete category'));
      }
      setDeleteModal({ open: false, id: null });
    }
  };

  const columns = [
    {
      key: 'name',
      title: 'Category',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          {row.coverImageUrl || row.image_url ? (
            <img src={row.coverImageUrl || row.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
          ) : (
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <FolderIcon className="w-5 h-5 text-indigo-600" />
            </div>
          )}
          <div>
            <p className="font-medium text-slate-800">{value || 'Unnamed'}</p>
            <p className="text-sm text-slate-500 line-clamp-1">{row.description || 'No description'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'articleCount',
      title: 'Articles',
      render: (value) => <Badge variant="info">{value || 0} articles</Badge>,
    },
    {
      key: 'priority',
      title: 'Priority',
      render: (value) => <span className="text-slate-600">{value || 0}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => <Badge variant={value === 'active' ? 'success' : 'warning'} dot>{value || 'Active'}</Badge>,
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openModal(row)} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit">
            <PencilIcon className="w-4 h-4 text-slate-500" />
          </button>
          <button onClick={() => setDeleteModal({ open: true, id: row.id })} className="p-2 hover:bg-red-50 rounded-lg" title="Delete">
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Categories</h1>
          <p className="text-slate-500">Organize your content with categories</p>
        </div>
        <Button icon={PlusIcon} onClick={() => openModal()}>New Category</Button>
      </div>

      <Card noPadding>
        <div className="p-4 border-b border-slate-100">
          <SearchInput value={search} onChange={setSearch} placeholder="Search categories..." className="max-w-md" />
        </div>

        <Table columns={columns} data={categories} loading={loading} emptyMessage="No categories found" emptyIcon={FolderIcon} />

        {pagination && pagination.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </Card>

      {/* Category Modal */}
      <CategoryModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        category={editingCategory}
      />

      <ConfirmDialog isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })} onConfirm={handleDelete} title="Delete Category" message="Are you sure you want to delete this category?" type="danger" confirmText="Delete" />
    </div>
  );
};

export default CategoryList;
